<template>
  <div :class="$style.wrapper">
    <div :class="$style.formRow">
      <div :class="$style.formField">
        <label for="offerTitle">Offer type</label>
        <Select
          id="offerTitle"
          v-model="offerType"
          :options="OFFER_TYPES"
          :class="$style.dropdown"
          :disabled="!isEditable"
          option-disabled="disabled"
          option-label="label"
          option-value="id"
          placeholder="Select an offer type"
          :pt="{
            input: {
              style: {
                fontSize: '12px',
                padding: '0.6rem 0.75rem',
              },
            },
          }"
          @update:model-value="handleOfferProductTypeUpdate"
        />
      </div>
      <div v-if="offerType === 'SPIN'" :class="$style.formField">
        <label for="offerTitle">Offer subtype</label>
        <Select
          id="offerTitle"
          v-model="offerSubtype"
          :options="OFFER_SUBTYPES"
          :class="$style.dropdown"
          :disabled="!isEditable"
          placeholder="Select an offer subtype"
          :pt="{
            input: {
              style: {
                fontSize: '12px',
                padding: '0.6rem 0.75rem',
              },
            },
          }"
          @update:model-value="handleOfferSubtypeChange"
        />
      </div>
      <div v-if="offerType === 'NPO'" :class="$style.inputSwitchWrapper">
        <ToggleSwitch
          :model-value="isEndlessOffer"
          :disabled="!isEditable"
          @change="handleEndlessUpdate"
        ></ToggleSwitch>
        <label style="margin-top: 0.2rem" for="offerEndless">Is endless</label>
      </div>
    </div>
    <div>
      <Message v-if="!isOfferProductReady && getOfferProductReadinessMessage()" :closable="false" severity="warn">
        <span>{{ getOfferProductReadinessMessage() }}</span>
      </Message>
    </div>
    <PriceTable
      v-if="offerType === 'SPIN'"
      :offer-id="offerId"
      :is-editable="isEditable"
      :offer-subtype="offerSubtype"
      :offer-spin="(offerSpin?.getOfferSpin?.[0] as OfferSpin)"
      @refetch-offer-spin="handleRefetchOfferSpin"
    />
    <WheelOfFortuneTable
      v-if="offerType === 'SPIN'"
      :offer-id="offerId"
      :is-editable="isEditable"
      :offer-spin="(offerSpin?.getOfferSpin?.[0] as OfferSpin)"
      @is-offer-ready="isOfferReady"
      @refetch-offer-spin="handleRefetchOfferSpin"
    />
    <OfferProductTable
      v-else
      :offer-id="offerId"
      :offer-type="offerType"
      :is-editable="isEditable"
      :type="type"
      :offer-products="(result?.getOfferProducts as OfferProduct[])"
      @refetch-offer-products="refetchOfferProducts"
    />
    <section v-if="isTwoOrThreePO && flags.buyAll.enabled" :class="$style.buyAllWrapper">
      <div :class="$style.checkbox">
        <Checkbox
          v-model="isBuyAllEnabled"
          name="selectedDays"
          value="buyAll"
          binary
          :disabled="!isOfferProductReady || !isEditable"
        />
        <label for="buyAll">Enable "Buy All" on this offer</label>
      </div>
      <PriceTypeDropdown
        :selected-price-type="selectedBuyAllPriceType"
        offer-version="V2"
        :disabled="!isOfferProductReady || !isBuyAllEnabled || !isEditable"
        :buy-all-price="offerBuyAllDetails"
        style="width: 40%"
        :non-consumable-ids="nonConsumableIds"
        :is-buy-all-enabled="isBuyAllEnabled"
        @update-price-type="handlePriceTypeChange"
        @update-iap-id="handleIapIdChange"
        @update-rv-amount="handleRvAmountChange"
        @update-in-game-price="handleInGamePriceChange"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import {
  OfferBuyAllDetails,
  OfferProduct,
  OfferSpin,
  OfferSpinUpdateInput,
  OfferType,
  SellableProductPriceType,
  SpinType,
  useCreateOfferSpinMutation,
  useDeleteOfferProductMutation,
  useDeleteOfferSpinMutation,
  useGetOfferProductsQuery,
  useGetOfferSpinQuery,
  useUpdateOfferSpinMutation,
} from "@/graphql";
import Checkbox from "primevue/checkbox";
import Message from "primevue/message";
import Select from "primevue/select";
import ToggleSwitch from "primevue/toggleswitch";
import { useConfirm } from "primevue/useconfirm";
import { PropType, computed, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";

import useFeatureFlags from "@/hooks/useFeatureFlags";
import OfferProductTable from "./PO/OfferProductTable.vue";
import PriceTable from "./WheelOfFortune/PriceTable.vue";
import WheelOfFortuneTable from "./WheelOfFortune/WheelOfFortuneTable.vue";
import PriceTypeDropdown from "./components/PriceTypeDropdown/PriceTypeDropdown.vue";
import { mapOfferSpinInput } from "./utils";

const emit = defineEmits([
  "is-offer-ready",
  "offer-product-type-update",
  "offer-endless-property-update",
  "offer-chained-property-update",
  "update-buy-all-details",
]);

const props = defineProps({
  offerId: {
    type: String,
    required: true,
  },
  isEditable: {
    type: Boolean,
    required: true,
  },
  type: {
    type: String as PropType<OfferType>,
    required: true,
  },
  isChained: {
    type: Boolean,
    required: true,
  },
  isEndless: {
    type: Boolean,
    required: true,
  },
  offerBuyAllDetails: {
    type: Object as PropType<OfferBuyAllDetails | null>,
    default: null,
  },
});

const isEndlessOffer = ref(false);
const offerType = ref<OfferType>(OfferType.OnePo);
const OFFER_SUBTYPES = ref(["Pay first", "Pay later"]);
const offerSubtype = ref("Pay first");
const selectedBuyAllPriceType = ref(SellableProductPriceType.Iap);

const buyAllPrice = reactive<Omit<OfferBuyAllDetails, "priceType">>({
  androidInGameCurrencyId: null,
  androidInGameCurrencyQuantity: null,
  iosInGameCurrencyId: null,
  iosInGameCurrencyQuantity: null,
  androidIAPId: null,
  iosIAPId: null,
  androidRVQuantity: null,
  iosRVQuantity: null,
});

const isTwoOrThreePO = computed(() => offerType.value === OfferType.TwoPo || offerType.value === OfferType.ThreePo);

const confirm = useConfirm();
const route = useRoute();
const appId = ref<string>(route.params.gameId as string);
const versionId = ref<string>(route.params.versionId as string);

const { flags } = useFeatureFlags(appId.value);

const apiRequestsPayload = {
  appId: appId.value,
  version: versionId.value,
  offerId: props.offerId,
};

const { mutate: createOfferSpin } = useCreateOfferSpinMutation({});
const { mutate: deleteOfferSpin } = useDeleteOfferSpinMutation({});
const { mutate: updateOfferSpin } = useUpdateOfferSpinMutation({});
const { result: offerSpin, refetch: refetchOfferSpin } = useGetOfferSpinQuery(
  { ...apiRequestsPayload },
  { fetchPolicy: "network-only" }
);

const { mutate: deleteOfferProduct } = useDeleteOfferProductMutation({});
const { result, refetch: refetchOfferProducts } = useGetOfferProductsQuery(
  { ...apiRequestsPayload },
  { fetchPolicy: "network-only" }
);

const nonConsumableIds = computed(() => {
  const offerProducts = result.value?.getOfferProducts || [];

  return offerProducts.flatMap(
    (product) => product?.items?.filter((item) => item.item?.type === "NON_CONSUMABLE").map((item) => item.itemId) ?? []
  );
});

const isBuyAllEnabled = ref(false);

const OFFER_TYPES = computed(() => [
  {
    id: OfferType.OnePo,
    label: "PO",
    disabled: result?.value?.getOfferProducts && result.value?.getOfferProducts.length > 1,
  },
  {
    id: OfferType.TwoPo,
    label: "2 PO",
    disabled: result?.value?.getOfferProducts && result.value?.getOfferProducts.length > 2,
  },
  {
    id: OfferType.ThreePo,
    label: "3 PO",
    disabled: result?.value?.getOfferProducts && result.value?.getOfferProducts.length > 3,
  },
  {
    id: OfferType.Npo,
    label: "Chained",
    disabled: false,
  },
  {
    id: OfferType.Spin,
    label: "Wheel of Fortune",
  },
]);

const handleOfferProductTypeUpdate = (value: OfferType) => {
  offerType.value = value;
};

const isOfferReady = (value: boolean) => {
  emit("is-offer-ready", value);
};

const modalContent = {
  header: "Confirmation",
  rejectClass: "p-button-secondary p-button-outlined",
  acceptClass: "p-button-danger",
  acceptIcon: "pi pi-trash",
  rejectLabel: "No",
  acceptLabel: "Yes",
};

const getOfferProductReadinessMessage = () => {
  switch (offerType.value) {
    case OfferType.OnePo:
      return "1 PO offer must have 1 product configured";
    case OfferType.TwoPo:
      return "2 PO offer must have 2 products configured";
    case OfferType.ThreePo:
      return "3 PO offer must have 3 products configured";
    case OfferType.Npo:
      return "Chained offer must have at least 1 product configured";
    case OfferType.Spin:
      return "Wheel of Fortune offer must include at least two options, and their probabilities should total 100%.";
    default:
      return "";
  }
};

const updateOfferBuyAllDetails = () => {
  if (isBuyAllEnabled.value) {
    emit("update-buy-all-details", {
      offerBuyAllDetails: {
        priceType: selectedBuyAllPriceType.value || SellableProductPriceType.Iap,
        ...buyAllPrice,
      },
    });
  } else {
    selectedBuyAllPriceType.value = SellableProductPriceType.Iap;
    emit("update-buy-all-details", {
      offerBuyAllDetails: null,
    });
  }
};

interface IapIds {
  ios: string;
  android: string;
}

const handleIapIdChange = (ids: IapIds) => {
  buyAllPrice.iosIAPId = ids.ios;
  buyAllPrice.androidIAPId = ids.android;
  updateOfferBuyAllDetails();
};

interface RvPrices {
  ios: number;
  android: number;
}

const handleRvAmountChange = (prices: RvPrices) => {
  buyAllPrice.iosRVQuantity = prices.ios;
  buyAllPrice.androidRVQuantity = prices.android;
  updateOfferBuyAllDetails();
};

interface Price {
  quantity: number;
  itemId: string;
}

interface InGameCurrencyPrices {
  ios: Price;
  android: Price;
}

const handleInGamePriceChange = (prices: InGameCurrencyPrices) => {
  buyAllPrice.iosInGameCurrencyId = prices.ios.itemId;
  buyAllPrice.iosInGameCurrencyQuantity = prices.ios.quantity;
  buyAllPrice.androidInGameCurrencyId = prices.android.itemId;
  buyAllPrice.androidInGameCurrencyQuantity = prices.android.quantity;
  updateOfferBuyAllDetails();
};

const resetBuyAllPriceTypeValues = () => {
  buyAllPrice.androidInGameCurrencyId = null;
  buyAllPrice.androidInGameCurrencyQuantity = null;
  buyAllPrice.iosInGameCurrencyId = null;
  buyAllPrice.iosInGameCurrencyQuantity = null;
  buyAllPrice.androidIAPId = null;
  buyAllPrice.iosIAPId = null;
  buyAllPrice.androidRVQuantity = null;
  buyAllPrice.iosRVQuantity = null;
};

const handlePriceTypeChange = (value: SellableProductPriceType) => {
  selectedBuyAllPriceType.value = value;
  resetBuyAllPriceTypeValues();

  if (value === SellableProductPriceType.Free) {
    updateOfferBuyAllDetails();
  }
};

const handleRefetchOfferSpin = async () => {
  await refetchOfferSpin();
};

const emitIsEndlessUpdate = () => {
  emit("offer-endless-property-update", isEndlessOffer.value);
};

watch(
  () => props.type,
  (value, oldValue) => {
    offerType.value = value;
    if (offerType.value === OfferType.Npo) {
      emit("offer-chained-property-update", true);
    } else if (oldValue === OfferType.Npo) {
      isEndlessOffer.value = false;
      emit("offer-chained-property-update", false);
      emitIsEndlessUpdate();
    }
  },
  {
    immediate: true,
  }
);

watch(
  () => props.isEndless,
  (value) => {
    isEndlessOffer.value = value;
  },
  {
    immediate: true,
  }
);

const handleCreateOfferSpin = async () => {
  const payload = {
    input: {
      rank: 1,
      type: offerSubtype.value === "Pay first" ? SpinType.PayFirst : SpinType.SpinFirst,
      priceType: SellableProductPriceType.Free,
      reRollPriceType: SellableProductPriceType.Free,
      reRollLimit: 0,
      androidPrice: {
        IAPId: null,
        rvQuantity: null,
        inGamePrice: null,
      },
      iosPrice: {
        IAPId: null,
        rvQuantity: null,
        inGamePrice: null,
      },
      reRollAndroidPrice: {
        IAPId: null,
        rvQuantity: null,
        inGamePrice: null,
      },
      reRollIosPrice: {
        IAPId: null,
        rvQuantity: null,
        inGamePrice: null,
      },
    },
  };

  await createOfferSpin({ ...apiRequestsPayload, ...payload });
  await refetchOfferSpin();
};

const offerPOsCategory = [OfferType.OnePo, OfferType.TwoPo, OfferType.ThreePo, OfferType.Npo];

watch(
  () => offerType.value,
  async (newValue, oldValue) => {
    const isSpinPreviousOfferType = oldValue === OfferType.Spin;
    const hasOfferSpinItems = offerSpin.value?.getOfferSpin?.[0]?.items;
    const hasOfferProducts = result.value?.getOfferProducts;

    if (offerSpin.value?.getOfferSpin && newValue === OfferType.Spin && !offerSpin.value?.getOfferSpin?.[0]) {
      handleCreateOfferSpin();
    }

    if (isSpinPreviousOfferType) {
      if (hasOfferSpinItems === undefined || (hasOfferSpinItems && hasOfferSpinItems.length === 0)) {
        emit("offer-product-type-update", newValue);
        return;
      }

      confirm.require({
        ...modalContent,
        message: "If you switch to a PO type offer, all your rewards will be lost. Are you sure you want to proceed?",
        accept: async () => {
          await deleteOfferSpin({ ...apiRequestsPayload, offerSpinId: offerSpin.value?.getOfferSpin?.[0]?.id || "" });
          await refetchOfferSpin();
          emit("offer-product-type-update", newValue);
        },
        reject: () => {
          offerType.value = oldValue;
        },
      });
    } else {
      if (
        offerPOsCategory.includes(oldValue) &&
        (hasOfferProducts?.length === 0 || offerPOsCategory.includes(newValue))
      ) {
        emit("offer-product-type-update", newValue);
        return;
      }

      confirm.require({
        ...modalContent,
        message:
          "If you switch to a Wheel of Fortune type offer, all your rewards will be lost. Are you sure you want to proceed?",
        accept: async () => {
          const offerProductsIds = hasOfferProducts?.map((offer) => offer?.id || "") || [];

          for (const offerProductId of offerProductsIds) {
            /* eslint-disable no-await-in-loop */
            await deleteOfferProduct({ ...apiRequestsPayload, offerProductId });
          }

          await refetchOfferProducts();

          emit("offer-product-type-update", newValue);
        },
        reject: () => {
          offerType.value = oldValue;
        },
      });
    }
  }
);

const handleOfferSubtypeChange = async (newSubType: string) => {
  const newType = newSubType === "Pay first" ? SpinType.PayFirst : SpinType.SpinFirst;

  if (!offerSpin.value?.getOfferSpin?.[0] || newType === offerSpin.value?.getOfferSpin?.[0].type) {
    return;
  }

  const input = mapOfferSpinInput(offerSpin.value?.getOfferSpin?.[0] as OfferSpin) as OfferSpinUpdateInput;

  if (newSubType === "Pay first") {
    input.reRollAndroidPrice = {
      IAPId: null,
      rvQuantity: null,
      inGamePrice: null,
    };
    input.reRollIosPrice = {
      IAPId: null,
      rvQuantity: null,
      inGamePrice: null,
    };
    input.reRollPriceType = SellableProductPriceType.Free;
    input.reRollLimit = 0;
  }

  const payload = {
    offerSpinId: offerSpin.value?.getOfferSpin?.[0]?.id || "",
    input: {
      ...input,
      id: offerSpin.value?.getOfferSpin?.[0]?.id,
      type: newType,
    },
  };

  offerSubtype.value = newSubType;

  await updateOfferSpin({ ...apiRequestsPayload, ...payload });
  await refetchOfferSpin();
};

const isSpinOfferReady = computed(() => {
  const offerSpinItems = offerSpin.value?.getOfferSpin?.[0]?.items || [];
  const totalProbability = offerSpinItems.reduce((acc, product) => {
    const probability = product?.probabilityPercentage || 0;
    return acc + probability;
  }, 0);

  const hasTwoItems = offerSpinItems.length >= 2;
  return totalProbability === 100 && hasTwoItems;
});

const isOfferProductReady = computed(
  () =>
    result.value?.getOfferProducts &&
    ((result.value?.getOfferProducts?.length === 1 && offerType.value === OfferType.OnePo) ||
      (result.value?.getOfferProducts?.length === 2 && offerType.value === OfferType.TwoPo) ||
      (result.value?.getOfferProducts?.length === 3 && offerType.value === OfferType.ThreePo) ||
      (result.value?.getOfferProducts?.length > 0 && offerType.value === OfferType.Npo) ||
      (isSpinOfferReady.value && offerType.value === OfferType.Spin))
);

const isBuyAllPriceSet = computed(() => {
  if (isBuyAllEnabled.value) {
    if (selectedBuyAllPriceType.value === SellableProductPriceType.Free) {
      return true;
    }
    const { priceType, __typename, ...rest } = props.offerBuyAllDetails || {};
    return Object.values(rest).some((value) => value !== null);
  }
  return true;
});

watch(
  () => [isOfferProductReady, isBuyAllPriceSet],
  () => {
    emit("is-offer-ready", isOfferProductReady.value && isBuyAllPriceSet.value);
  },
  {
    immediate: true,
    deep: true,
  }
);

watch(
  () => offerSpin.value?.getOfferSpin?.[0]?.type,
  () => {
    if (offerSpin.value?.getOfferSpin?.[0]?.type === SpinType.SpinFirst) {
      offerSubtype.value = "Pay later";
    } else {
      offerSubtype.value = "Pay first";
    }
  },
  {
    immediate: true,
  }
);

const handleEndlessUpdate = () => {
  isEndlessOffer.value = !isEndlessOffer.value;
  emitIsEndlessUpdate();
};

watch(
  () => props.offerBuyAllDetails,
  () => {
    isBuyAllEnabled.value = !!props.offerBuyAllDetails;
    selectedBuyAllPriceType.value = props.offerBuyAllDetails?.priceType as SellableProductPriceType;
  },
  { immediate: true }
);

watch(
  () => isBuyAllEnabled.value,
  () => {
    if (!isBuyAllEnabled.value) {
      resetBuyAllPriceTypeValues();
      updateOfferBuyAllDetails();
      selectedBuyAllPriceType.value = SellableProductPriceType.Iap;
    }
  }
);
</script>

<style module lang="scss">
.inputSwitchWrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.7rem;
  width: 200px;
  margin-top: 1.7rem;
}

.wrapper {
  padding: 1rem;
  width: 100%;
}

.formRow {
  display: flex;
  gap: 1rem;
}

.buyAllWrapper {
  display: flex;
  align-items: center;
}

.checkbox {
  display: flex;
  align-items: center;
  margin: 1rem;
  white-space: nowrap;

  label {
    font-size: 14px;
    color: var(--vd-color-text-light-2);
    margin-left: 0.5rem;
  }
}

.formField {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: auto;
  min-width: 200px;

  label {
    font-size: 12px;
    color: var(--vd-color-text-default);
  }

  .dropdown {
    border: 1px solid var(--vd-color-neutral-light-2);
    font-size: 12px;
    color: var(--vd-color-text-dark);
    width: 100%;
    border-radius: 8px;
    height: 32px;
    max-width: 208px;
  }
}
</style>
